import { Component, type ErrorInfo, type ReactNode } from "react";
import { errorLogger } from "@/lib/errorLogger";

// Detects chunk/dynamic-import load failures (stale deployment, network blip)
function isChunkLoadError(error: Error): boolean {
  const msg = error.message || "";
  return (
    msg.includes("is not a valid JavaScript MIME type") ||
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Loading chunk") ||
    msg.includes("error loading dynamically imported module")
  );
}

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Auto-reload once for chunk load failures (stale deployment)
    if (isChunkLoadError(error)) {
      const hasRetried = sessionStorage.getItem("eb_chunk_retry");
      if (!hasRetried) {
        sessionStorage.setItem("eb_chunk_retry", "1");
        window.location.reload();
        return;
      }
      sessionStorage.removeItem("eb_chunk_retry");
    }

    console.error("ErrorBoundary caught:", error, info.componentStack);
    errorLogger.log({
      error_type: "render",
      severity: "critical",
      message: error.message,
      stack_trace: error.stack || null,
      component_name: info.componentStack?.slice(0, 500) || null,
      action_context: "React render error boundary",
    });
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-6">
          <div className="w-full max-w-md rounded-lg border bg-card p-8 text-center shadow-sm">
            <h2 className="mb-2 text-xl font-semibold text-foreground">
              Something went wrong
            </h2>
            <p className="mb-6 text-sm text-muted-foreground">
              An unexpected error occurred. Please reload the page to continue.
            </p>
            {this.state.error && (
              <pre className="mb-6 max-h-32 overflow-auto rounded bg-muted p-3 text-left text-xs text-muted-foreground">
                {this.state.error.message}
              </pre>
            )}
            <button
              onClick={this.handleReload}
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
